import type { EpisodeRef, ProjectDetail, StoryboardView } from "@dramapie/shared";
import type { RefObject } from "react";
import { Link } from "react-router-dom";

import { STREAM_STATUS_LABEL, type StreamStatus } from "../../lib/stream";
import { episodeGates } from "../../lib/view";
import {
  CONTENT_VIEW_LABEL,
  isContentView,
  WORKSPACE_TABS,
  type WorkspaceTab,
} from "../../lib/workspace-tabs";
import { ExportMenu } from "./ExportMenu";

export interface TopbarProps {
  project: ProjectDetail | null;
  /** 当前产出线标题（M8）：左栏切换后面包屑跟着换。缺省回落到第一条。 */
  outputTitle?: string | null;
  view: StoryboardView | null;
  activeEpisodeId: string | undefined;
  /** 当前产出线的集（M8）：顶栏的集下拉只列这条线的集，别把别的线混进来。 */
  episodes?: readonly EpisodeRef[];
  onSelectEpisode: (episodeId: string) => void;
  /** 当前主区视图：只影响面包屑；顶栏 tab 只有三个投影视图。 */
  viewMode: WorkspaceTab;
  onChangeView: (mode: WorkspaceTab) => void;
  pending: boolean;
  streamStatus: StreamStatus;
  pointsLabel: string;
  inspectorOpen: boolean;
  onToggleInspector: () => void;
  onToggleTheme: () => void;
  /** 收起状态下的「Agent」入口；焦点归还也落在这里。 */
  agentButtonRef?: RefObject<HTMLButtonElement>;
  /** Agent 正在执行时给入口点亮小圆点。 */
  agentBusy?: boolean;
  /** 撤销：有可撤销的提交才可用（契约 M4 §1）。 */
  canUndo: boolean;
  undoPending: boolean;
  undoHint: string;
  onUndo: () => void;
  /** 导出：zip 后台打包 / 打印故事板。 */
  exportBusy: boolean;
  exportDisabled: boolean;
  exportDisabledHint: string;
  onExportZip: () => void;
  onPrintStoryboard: () => void;
  /** 分享：只读外链弹层（契约 M5 §2）。 */
  onOpenShare: () => void;
}

const TAB_TITLE: Partial<Record<WorkspaceTab, string>> = {
  storyboard: "故事板：编辑镜头（快捷键 1）",
  canvas: "画布：场容器 + 镜头卡（快捷键 2）",
  timeline: "时间线：画面与台词轨（快捷键 3）",
};

/**
 * 顶栏（64px）：面包屑 / 三个投影视图 / 状态胶囊 / 余额 / 右栏开关。
 *
 * 故事板 / 画布 / 时间线是同一份数据的三种投影，所以并排放在顶栏；
 * 资料库 / 大纲 / 剧本 / 数据是「另一份内容」，入口在左栏「内容导航」，
 * 这里只在面包屑上显示当前内容名，不再占用顶栏 tab。
 */
export function Topbar({
  project,
  outputTitle: outputTitleProp,
  view,
  episodes: episodesProp,
  activeEpisodeId,
  onSelectEpisode,
  viewMode,
  onChangeView,
  pending,
  streamStatus,
  pointsLabel,
  inspectorOpen,
  onToggleInspector,
  onToggleTheme,
  agentButtonRef,
  agentBusy = false,
  canUndo,
  undoPending,
  undoHint,
  onUndo,
  exportBusy,
  exportDisabled,
  exportDisabledHint,
  onExportZip,
  onPrintStoryboard,
  onOpenShare,
}: TopbarProps) {
  const episodeList = episodesProp ?? project?.episodes ?? [];
  const episode: EpisodeRef | undefined = (project?.episodes ?? episodeList).find(
    (item) => item.id === activeEpisodeId,
  );
  const gates = episodeGates(view?.shots ?? []);
  const currentLabel = view
    ? `第 ${view.episode.no} 话 · ${view.episode.title}`
    : episode
      ? `第 ${episode.no} 话 · ${episode.title}`
      : "暂无剧集";
  const epCode = view ? `EP ${view.episode.no}` : "EP —";
  const outputTitle = outputTitleProp ?? project?.outputs[0]?.title ?? "产出线";
  /** 内容视图（左栏进入）用内容名做面包屑；三个投影仍按产出线 → 集显示。 */
  const contentLabel = isContentView(viewMode) ? CONTENT_VIEW_LABEL[viewMode] : null;

  return (
    <header className="dp-topbar">
      <nav className="dp-crumbs" aria-label="面包屑">
        <Link to="/" className="dp-crumb" aria-label="返回项目列表">
          {project?.title ?? "项目"}
        </Link>
        <span className="dp-crumb-sep dp-crumb-output-sep" aria-hidden="true">
          /
        </span>
        {contentLabel ? (
          <span className="dp-crumb-now">
            {contentLabel}
            <em>内容</em>
          </span>
        ) : (
          <>
            <span className="dp-crumb dp-crumb-output">{outputTitle}</span>
            <span className="dp-crumb-sep" aria-hidden="true">
              /
            </span>
            <span className="dp-crumb-now">
              {currentLabel}
              <em>{epCode}</em>
            </span>
          </>
        )}
      </nav>

      <div className="dp-top-center">
        <div className="dp-seg" role="group" aria-label="视图切换">
          {WORKSPACE_TABS.map((tab) => {
            const current = viewMode === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                aria-current={current ? "page" : undefined}
                aria-keyshortcuts={tab.key}
                title={TAB_TITLE[tab.id] ?? tab.label}
                onClick={() => onChangeView(tab.id)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="dp-top-right">
        {contentLabel ? null : (
          <div className="dp-gates">
            <span
              className={`dp-gate-chip is-${gates.visual.tone}`}
              title="视觉门禁：由镜头状态推导"
            >
              <i className="dot" aria-hidden="true" />
              {gates.visual.label}
            </span>
            <span
              className={`dp-gate-chip is-${gates.final.tone}`}
              title="成片门禁：由视频槽位推导"
            >
              <i className="dot" aria-hidden="true" />
              {gates.final.label}
            </span>
          </div>
        )}

        <span
          className={`dp-save-pill${pending ? " is-busy" : ""}`}
          aria-live="polite"
          title={`实时连接：${STREAM_STATUS_LABEL[streamStatus]}`}
        >
          {pending ? "保存中…" : streamStatus === "open" ? "实时" : "已保存"}
        </span>

        <select
          id="episode-switch"
          aria-label="选择集数"
          value={activeEpisodeId ?? ""}
          onChange={(event) => onSelectEpisode(event.target.value)}
          disabled={episodeList.length === 0}
          className="dp-episode-select"
        >
          {episodeList.map((item) => (
            <option key={item.id} value={item.id}>
              第 {item.no} 集 · {item.title}
            </option>
          ))}
        </select>

        <span className="dp-budget-mini" aria-live="polite" title="点数余额">
          点数 {pointsLabel}
        </span>

        <button
          type="button"
          className="dp-btn sm dp-undo-btn"
          onClick={onUndo}
          disabled={!canUndo || undoPending}
          title={undoPending ? "正在撤销上一个提交…" : undoHint}
          aria-keyshortcuts="Control+Z Meta+Z"
        >
          {undoPending ? "撤销中…" : "撤销"}
          <span className="dp-undo-key" aria-hidden="true">
            ⌘Z
          </span>
        </button>

        <button
          type="button"
          className="dp-btn sm dp-share-btn"
          onClick={onOpenShare}
          aria-haspopup="dialog"
          title="生成只读分享链接"
        >
          分享
        </button>

        <ExportMenu
          busy={exportBusy}
          disabled={exportDisabled}
          disabledHint={exportDisabledHint}
          onExportZip={onExportZip}
          onPrint={onPrintStoryboard}
        />

        <button
          type="button"
          className="dp-icon-btn"
          aria-label="切换主题"
          title="浅色施工中"
          onClick={onToggleTheme}
        >
          ◐
        </button>
        {inspectorOpen ? (
          <button
            type="button"
            className="dp-icon-btn"
            aria-label="收起右栏"
            aria-pressed
            aria-controls="workspace-inspector"
            title="收起右栏"
            onClick={onToggleInspector}
          >
            ▥
          </button>
        ) : (
          <button
            ref={agentButtonRef}
            type="button"
            className="dp-agent-btn"
            aria-label="打开 Agent 面板"
            aria-expanded={false}
            aria-controls="workspace-inspector"
            title={agentBusy ? "打开 Agent 面板（Agent 正在执行）" : "打开 Agent 面板"}
            onClick={onToggleInspector}
          >
            <span
              className={`dp-agent-dot${agentBusy ? " is-busy" : ""}`}
              aria-hidden="true"
            />
            Agent
            <span className="dp-sr">{agentBusy ? "Agent 正在执行" : ""}</span>
          </button>
        )}
      </div>
    </header>
  );
}
